import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { money } from "../../lib/api";

const TOOLTIP = { background: "#0b0f19", border: "1px solid rgba(212,175,55,.3)" };
const CHART_MARGIN = { bottom: 18 };
const LEGEND_STYLE = { fontSize: 11, color: "#94a3b8" };
const BAR_RADIUS = [3, 3, 0, 0];

const Metric = ({ label, value, testid }) => (
  <div className="hy-glass p-5" data-testid={testid}>
    <div className="hy-label text-[0.55rem]">{label}</div>
    <div className="font-display text-xl text-amber-200 mt-2">{value}</div>
  </div>
);

export const PromoInsights = ({ insights }) => {
  if (!insights) return null;
  const { totals, codes } = insights;
  const redeemed = codes.filter((c) => c.registrations > 0);
  const topCodes = [...redeemed].sort((a, b) => b.net_revenue - a.net_revenue).slice(0, 8);

  return (
    <div className="space-y-6" data-testid="promo-insights">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Codes Redeemed" value={`${totals.codes_redeemed} / ${totals.codes_total}`} testid="promo-metric-redeemed" />
        <Metric label="Registrations With A Code" value={`${totals.registrations_with_code} (${totals.promo_share}%)`} testid="promo-metric-share" />
        <Metric label="Discount Given" value={money(totals.discount_given)} testid="promo-metric-discount" />
        <Metric label="Net Revenue From Codes" value={money(totals.net_revenue_with_code)} testid="promo-metric-net" />
      </div>

      {topCodes.length > 0 && (
        <div className="hy-card p-7">
          <div className="hy-label mb-2">
            Net revenue vs discount cost — top {topCodes.length} code{topCodes.length > 1 ? "s" : ""}
          </div>
          <p className="text-xs text-slate-500 mb-6">
            Ranked by net revenue. Discount cost uses the right axis so small discounts stay readable.
          </p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={topCodes} margin={CHART_MARGIN}>
              <XAxis dataKey="code" stroke="#94a3b8" fontSize={10} interval={0} angle={-18} textAnchor="end" height={50} />
              <YAxis yAxisId="net" stroke="#d4af37" fontSize={11} />
              <YAxis yAxisId="disc" orientation="right" stroke="#a78bfa" fontSize={11} />
              <Tooltip contentStyle={TOOLTIP} formatter={(v) => money(v)} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar yAxisId="net" dataKey="net_revenue" name="Net revenue" fill="#d4af37" radius={BAR_RADIUS} />
              <Bar yAxisId="disc" dataKey="discount" name="Discount cost" fill="#7c3aed" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="hy-card overflow-x-auto" data-testid="promo-insights-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.6rem] uppercase tracking-widest text-slate-500 border-b border-amber-500/15">
              {["Code", "Registrations", "Editions", "Net Revenue", "Discount Cost", "Avg Discount", "Discount Rate", "Redemption", "Last Used"]
                .map((h) => <th key={h} className="p-4 font-normal">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.code} className="border-b border-amber-500/8" data-testid={`promo-insight-${c.code}`}>
                <td className="p-4">
                  <div className="font-mono-hy text-amber-200">{c.code}</div>
                  <div className="text-[0.6rem] uppercase tracking-widest mt-1 text-slate-500">
                    {c.percent_off > 0 ? `${c.percent_off}% off` : ""}{c.flat_off > 0 ? ` ${money(c.flat_off)} off` : ""}
                    {c.active ? "" : " · inactive"}
                  </div>
                </td>
                <td className="p-4 text-slate-200">{c.registrations}</td>
                <td className="p-4 text-xs text-slate-500">
                  {c.by_edition.length ? c.by_edition.map((e) => `${e.edition} ${e.count}`).join(" · ") : "—"}
                </td>
                <td className="p-4 text-emerald-300">{money(c.net_revenue)}</td>
                <td className="p-4 text-violet-300">{money(c.discount)}</td>
                <td className="p-4 text-slate-400">{money(c.avg_discount)}</td>
                <td className="p-4 text-slate-400">{c.discount_rate}%</td>
                <td className="p-4 text-slate-400">{c.used_count}/{c.max_uses} ({c.redemption_rate}%)</td>
                <td className="p-4 text-xs text-slate-500">{(c.last_used_at || "").slice(0, 10) || "never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {codes.length === 0 && <p className="p-8 text-slate-500 text-sm">No promotional codes yet.</p>}
      </div>

      <p className="text-[0.7rem] text-slate-500 leading-relaxed">
        {totals.registrations_without_code} registrations came in without a code, worth{" "}
        {money(totals.net_revenue_without_code)} net.
      </p>
    </div>
  );
};
